import React, { memo, useCallback, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { OUTLINE_BADGE } from 'app/services/formula/expressionOutline/expressionOutline.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  getBadgeStyles,
  getRowStyles,
  getRowTextStyles,
  outlineChevronButtonStyles,
  outlinePanelStyles,
  outlineTitleStyles,
} from './OutlineNavigator.styles';

const EMPTY_PROBLEM_IDS = new Set();

const pluralizeRows = count => `${count} row${count === 1 ? '' : 's'}`;

const nodePropType = PropTypes.shape({
  children: PropTypes.array.isRequired,
  depth: PropTypes.number.isRequired,
  id: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  badge: PropTypes.string,
  end: PropTypes.number,
  start: PropTypes.number,
});

const OutlineRow = memo(
  ({
    node,
    activeId,
    problemIds,
    collapsedIds,
    parentBadge,
    onNodeClick,
    onNodeHover,
    onToggleCollapse,
  }) => {
    const hasPosition = _.isNumber(node.start);
    const hasProblem = problemIds.has(node.id);
    const isActive = activeId === node.id;
    const isComparison = node.kind === 'comparison';
    const hasChildren = node.children.length > 0;
    const isCollapsed = hasChildren && collapsedIds.has(node.id);
    const badge = hasProblem ? OUTLINE_BADGE.ERR : node.badge;

    const handleClick = useCallback(() => {
      if (!hasPosition) {
        return;
      }

      onNodeClick(node);
    }, [hasPosition, node, onNodeClick]);

    const handleMouseEnter = useCallback(() => {
      onNodeHover(node);
    }, [node, onNodeHover]);

    const handleMouseLeave = useCallback(() => {
      onNodeHover(null);
    }, [onNodeHover]);

    const handleChevronClick = useCallback(
      event => {
        event.stopPropagation();
        onToggleCollapse(node.id);
      },
      [node.id, onToggleCollapse],
    );

    return (
      <>
        <Box
          sx={getRowStyles({
            depth: node.depth,
            hasPosition,
            isComparison,
            isActive,
            hasProblem,
            parentBadge,
          })}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {hasChildren && (
            <Box
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              component="span"
              role="button"
              sx={outlineChevronButtonStyles}
              onClick={handleChevronClick}
            >
              <Typography component="span" variant="regular12">
                {isCollapsed ? '▶' : '▼'}
              </Typography>
            </Box>
          )}
          {badge && (
            <Box sx={getBadgeStyles(badge)}>
              <Typography variant="regular12">{badge}</Typography>
            </Box>
          )}
          <Typography sx={getRowTextStyles(hasProblem)} variant="regular12">
            {node.label}
          </Typography>
        </Box>
        {!isCollapsed &&
          node.children.map(child => (
            <OutlineRow
              activeId={activeId}
              collapsedIds={collapsedIds}
              key={child.id}
              node={child}
              parentBadge={node.badge}
              problemIds={problemIds}
              onNodeClick={onNodeClick}
              onNodeHover={onNodeHover}
              onToggleCollapse={onToggleCollapse}
            />
          ))}
      </>
    );
  },
);

OutlineRow.displayName = 'OutlineRow';

OutlineRow.propTypes = {
  node: nodePropType.isRequired,
  problemIds: PropTypes.instanceOf(Set).isRequired,
  collapsedIds: PropTypes.instanceOf(Set).isRequired,
  onNodeClick: PropTypes.func.isRequired,
  onNodeHover: PropTypes.func.isRequired,
  onToggleCollapse: PropTypes.func.isRequired,
  activeId: PropTypes.string,
  parentBadge: PropTypes.string,
};

const OutlineNavigator = ({
  nodes,
  onNodeClick,
  onNodeHover = _.noop,
  activeId = null,
  problemIds = EMPTY_PROBLEM_IDS,
}) => {
  // The advanced navigator is opt-in (Show-outline toggle on the panel), so it
  // defaults to fully expanded; collapsing is self-managed per node id.
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  const handleToggleCollapse = useCallback(id => {
    setCollapsedIds(prev => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);

        return next;
      }

      next.add(id);

      return next;
    });
  }, []);

  const problemCount = problemIds.size;
  const hasProblems = problemCount > 0;

  return (
    <Box sx={outlinePanelStyles}>
      <Box sx={outlineTitleStyles}>
        <Typography color={hasProblems ? 'error.main' : undefined} variant="regular12">
          {hasProblems
            ? `Outline · ${pluralizeRows(problemCount)} have problems`
            : 'Outline · plain English'}
        </Typography>
      </Box>
      {nodes.map(node => (
        <OutlineRow
          activeId={activeId}
          collapsedIds={collapsedIds}
          key={node.id}
          node={node}
          problemIds={problemIds}
          onNodeClick={onNodeClick}
          onNodeHover={onNodeHover}
          onToggleCollapse={handleToggleCollapse}
        />
      ))}
    </Box>
  );
};

OutlineNavigator.propTypes = {
  nodes: PropTypes.arrayOf(nodePropType).isRequired,
  onNodeClick: PropTypes.func.isRequired,
  activeId: PropTypes.string,
  problemIds: PropTypes.instanceOf(Set),
  onNodeHover: PropTypes.func,
};

export default memo(OutlineNavigator);
